import { useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart } from '@/components/ui/icons';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useCart } from '@/contexts/cart/cart-context';
import { cn } from '@/lib/utils';
import { CART_ROUTE } from '@/views/marketplace/marketplaceRoutes';
import { MARKET_ROUTE } from '@/views/marketplace/constants';

/**
 * Menu-bar cart — the notification bell's twin, one slot to its left.
 *
 * Marketplace-only by design: the cart is meaningless on the editor or
 * Documents, and a permanent chrome icon that is empty everywhere else reads as
 * clutter. It returns `null` off `/market*` rather than hiding with CSS, so
 * nothing else in the app has to know it exists.
 *
 * Classes are deliberately copied from the bell (border-l, px-3, same amber
 * badge) instead of abstracted into a shared component: the two buttons are
 * neighbours whose *appearance* must match, not behaviour that should be
 * shared — the bell is a dropdown trigger, this navigates.
 */
export function CartMenuButton() {
  const navigate = useNavigate();
  const location = useLocation();
  const { count } = useCart();

  const onMarketplace =
    location.pathname === MARKET_ROUTE ||
    location.pathname.startsWith(`${MARKET_ROUTE}/`);
  if (!onMarketplace) return null;

  const isActive = location.pathname === CART_ROUTE;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label="Cart"
          data-testid="menubar-cart"
          onClick={() => navigate(CART_ROUTE)}
          className={cn(
            'relative flex h-full items-center justify-center border-l border-telekinesis-neutral-800 px-3 transition-colors',
            isActive
              ? 'bg-telekinesis-bg-dark text-telekinesis-text-primary'
              : 'text-telekinesis-text-secondary hover:bg-telekinesis-neutral-900 hover:text-telekinesis-text-primary',
          )}
        >
          <ShoppingCart className="h-3.5 w-3.5" />
          {count > 0 && (
            <span
              data-testid="menubar-cart-badge"
              className="absolute right-1.5 top-1.5 flex h-3.5 min-w-[14px] items-center justify-center rounded-full bg-[#FFBA28] px-0.5 font-mono text-[9px] font-bold leading-none text-black"
            >
              {count > 9 ? '9+' : count}
            </span>
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end">
        Cart
      </TooltipContent>
    </Tooltip>
  );
}
